import React, { useState } from 'react';
import {
  Container,
  Section,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Text,
  Input,
  Button,
  List,
  ListItem,
  Badge,
  Stack,
  Grid
} from '@tonic/shared/components/ui';
import { Search, FileText, Calendar, Tag } from 'lucide-react';

export function StandardReportsPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const standardReports = [
    {
      id: 1,
      name: 'Monthly Re-Commerce Summary',
      description: 'Comprehensive overview of re-commerce performance including returns, resales, rentals, and repairs.',
      category: 'Re-Commerce',
      lastRun: '2024-03-19'
    },
    {
      id: 2,
      name: 'AI Agent Performance',
      description: 'Analysis of AI agent interactions, response times, resolution rates, and customer satisfaction.',
      category: 'AI',
      lastRun: '2024-03-20'
    },
    {
      id: 3,
      name: 'Resale Profit by Category',
      description: 'Detailed breakdown of resale revenue and profit margins across different product categories.',
      category: 'Finance',
      lastRun: '2024-03-18'
    },
    {
      id: 4,
      name: 'Repair Turnaround Time',
      description: 'Statistics on repair processing times, bottlenecks, and efficiency metrics.',
      category: 'Operations',
      lastRun: '2024-03-20'
    },
    {
      id: 5,
      name: 'Customer Engagement Metrics',
      description: 'Analysis of customer interactions, feedback, and satisfaction across all channels.',
      category: 'Customer',
      lastRun: '2024-03-17'
    }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      'Re-Commerce': 'green',
      'AI': 'blue',
      'Finance': 'violet',
      'Operations': 'orange',
      'Customer': 'pink'
    };
    return colors[category] || 'gray';
  };

  const filteredReports = standardReports.filter(report =>
    report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Section>
        <Stack space={6}>
          <div>
            <Text variant="h1">Standard Reports</Text>
            <Text variant="body" className="mt-2 text-muted-foreground">
              Access and run pre-configured reports for business insights
            </Text>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Available Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <Stack space={4}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <List>
                  {filteredReports.map((report) => (
                    <ListItem key={report.id} className="p-4 hover:bg-muted/50 rounded-lg transition-colors">
                      <Stack space={3}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <Text className="font-medium">{report.name}</Text>
                          </div>
                          <Badge variant={getCategoryColor(report.category)}>
                            {report.category}
                          </Badge>
                        </div>

                        <Text variant="body" className="text-muted-foreground">{report.description}</Text>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <Text variant="body" className="text-muted-foreground">Last run: {report.lastRun}</Text>
                          </div>
                          <Button variant="outline" size="sm">
                            Run Report
                          </Button>
                        </div>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Section>
    </Container>
  );
} 
